import React from 'react';
import { useAcademyStore } from '../../store/academyStore';
import { motion } from 'framer-motion';
import { Trophy, Star, Shield, Cpu, Code, Database, Globe, Lock, Map, Verified as VerifiedIcon, Award, Printer, BookOpen, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { COURSES } from '../../data/curriculum';
import { getCourseIcon } from '../../types';
import { BackButton } from '../ui/BackButton';
import { ProgressRing } from '../ui/ProgressRing';

const badgesMeta: Record<string, { title: string, desc: string, icon: any, image: string }> = {
    'badge_digital-citizen': { title: 'Digital Citizen', desc: 'Stayed safe online!', icon: <Shield />, image: 'badge_digital-citizen.png' },
    'badge_foundation': { title: 'Foundation Explorer', desc: 'Mastered the basics.', icon: <Code />, image: 'badge_foundation.png' },
    'badge_production': { title: 'City Planner', desc: 'Built scalable systems.', icon: <Database />, image: 'badge_production.png' },
    'badge_data-detective': { title: 'Data Detective', desc: 'Solved the SQL mystery.', icon: <Globe />, image: 'badge_data-detective.png' },
    'badge_ai': { title: 'AI Operator', desc: 'Tamed the robots and agents.', icon: <Cpu />, image: 'badge_ai.png' },
    'badge_cyber-security': { title: 'Digital Guardian', desc: 'Protected the network.', icon: <Lock />, image: 'badge_cyber-security.png' },
    'badge_automation': { title: 'Bot Builder', desc: 'Automated the workflow.', icon: <Cpu />, image: 'badge_automation.png' },
    'badge_ui-ux': { title: 'Design Artist', desc: 'Painted the pixels.', icon: <Star />, image: 'badge_ui-ux.png' },
    'badge_systems': { title: 'Sovereign Ruler', desc: 'Gained control of the server rack.', icon: <Shield />, image: 'badge_systems.png' },
    'badge_game-dev': { title: 'Game Maker', desc: 'Built your first game!', icon: <Trophy />, image: 'badge_game-dev.png' },
    // Legacies
    'badge_variables': { title: 'Backpack Packer', desc: 'Packed the data.', icon: <Code />, image: 'badge_variables.png' },
    'badge_loops': { title: 'Loop Runner', desc: 'Ran the same track 100 times.', icon: <Code />, image: 'badge_loops.png' },
    'badge_architecture': { title: 'Map Reader', desc: 'Understands the client & server.', icon: <Map />, image: 'badge_architecture.png' },
    'badge_functions': { title: 'Recipe Chef', desc: 'Cooked up some logic.', icon: <Code />, image: 'badge_functions.png' },
    'badge_relational': { title: 'Architect', desc: 'Designed the blueprint.', icon: <Database />, image: 'badge_relational.png' },
    'badge_logic-architect': { title: 'Logic Architect', desc: 'Mastered React state and components.', icon: <Code />, image: 'badge_logic-architect.png' },
};

export const UserProfile = () => {
    const { currentUser, progress } = useAcademyStore();
    const navigate = useNavigate();

    if (!currentUser) return <div>Please login.</div>;

    const userProgress = progress[currentUser.id] || { completedModules: [], unlockedCourses: [], xp: 0, badges: [] };
    const earnedBadges = userProgress.badges || [];

    // Helper to check course completion
    const getCourseStatus = (courseId: string) => {
        const course = COURSES.find(c => c.id === courseId);
        if (!course) return { completed: false, count: 0, total: 0 };

        const moduleIds = course.modules.map(m => m.id);
        const completed = moduleIds.filter(id => userProgress.completedModules.includes(id));
        const isOfficiallyCompleted = userProgress.completedCourses?.includes(courseId) || !!userProgress.certificates?.[courseId];

        return {
            isComplete: isOfficiallyCompleted || (completed.length === moduleIds.length && moduleIds.length > 0),
            count: completed.length,
            total: moduleIds.length
        };
    };

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8 pb-20">
            {/* Header Card */}
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 flex flex-col md:flex-row items-center gap-8 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-green-50 rounded-full blur-3xl -mr-16 -mt-16 opacity-50"></div>

                <div className="relative z-10 w-32 h-32 rounded-full border-4 border-white shadow-xl overflow-hidden bg-slate-100">
                    <img
                        src={`/assets/gorillas/${currentUser.avatar}.png`}
                        alt="Profile"
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="flex-1 text-center md:text-left relative z-10">
                    <div className="mb-3 flex justify-center md:justify-start">
                        <BackButton to="/" label="Back to Dashboard" variant="inline" />
                    </div>
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                        {currentUser.username}
                    </h1>
                    <div className="flex items-center justify-center md:justify-start gap-4 text-slate-500">
                        <span className="flex items-center gap-1">
                            <VerifiedIcon size={16} className="text-green-500" />
                            Level {Math.floor(userProgress.xp / 1000) + 1} Scout
                        </span>
                    </div>

                    <div className="mt-4 flex gap-3 justify-center md:justify-start">
                        <div className="px-3 py-1 bg-green-100 text-green-700 rounded-lg font-mono text-sm border-2 border-green-200">
                            XP: {userProgress.xp}
                        </div>
                        <div className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg font-mono text-sm border-2 border-yellow-200">
                            Badges: {earnedBadges.length}
                        </div>
                    </div>
                </div>
            </div>

            {/* Badges Section */}
            <div>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <h2 className="text-2xl font-bold text-slate-700 flex items-center gap-2">
                        <Award className="text-yellow-500" />
                        Achievements
                    </h2>
                    
                    <button
                        onClick={() => navigate('/badges/export')}
                        disabled={earnedBadges.length === 0}
                        className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed w-fit"
                    >
                        <Printer size={16} />
                        Export to PDF
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(badgesMeta).map(([bId, meta]) => {
                        const isEarned = earnedBadges.includes(bId);

                        return (
                            <motion.div
                                key={bId}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: isEarned ? 1 : 0.8, y: 0 }}
                                className={`
                                    rounded-2xl p-6 flex items-center gap-6 transition-all border-2 relative overflow-hidden
                                    ${isEarned ? 'bg-white border-slate-100 shadow-sm hover:shadow-md' : 'bg-slate-50 border-dashed border-slate-200'}
                                `}
                            >
                                <div className={`
                                    w-24 h-24 flex-shrink-0 relative rounded-full border-4 shadow-sm overflow-hidden flex items-center justify-center
                                    ${isEarned ? 'border-white bg-slate-50' : 'border-slate-200 bg-slate-100'}
                                `}>
                                    {isEarned ? (
                                        <img
                                            src={`/assets/badges/${meta.image}`}
                                            alt={meta.title}
                                            className="w-full h-full object-cover transform scale-110 group-hover:scale-125 transition-transform duration-300"
                                        />
                                    ) : (
                                        <div className="text-slate-300">
                                            <Award size={40} />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h3 className={`text-xl font-bold font-comic mb-1 ${isEarned ? 'text-slate-800' : 'text-slate-400'}`}>
                                        {isEarned ? meta.title : 'Locked Badge'}
                                    </h3>
                                    <p className="text-sm text-slate-500 mb-2">
                                        {isEarned ? meta.desc : `Complete missions to earn the ${meta.title} badge!`}
                                    </p>

                                    {!isEarned && (
                                        <div className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded">
                                            <Lock size={12} /> LOCKED
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* MASTER DIPLOMA Section */}
            <div>
                <h2 className="text-2xl font-bold text-slate-700 mb-6 flex items-center gap-2 border-t border-slate-200 pt-8 mt-8">
                    <GraduationCap className="text-amber-500" />
                    Master Scout Diploma
                </h2>

                {(() => {
                    // Calculate overall completion
                    const courseStats = COURSES.map(course => {
                        const moduleIds = course.modules.map(m => m.id);
                        const completedCount = moduleIds.filter(id => userProgress.completedModules.includes(id)).length;
                        return { total: moduleIds.length, completed: completedCount };
                    });
                    const totalModules = courseStats.reduce((sum, c) => sum + c.total, 0);
                    const totalCompleted = courseStats.reduce((sum, c) => sum + c.completed, 0);
                    const overallProgress = totalModules > 0 ? Math.round((totalCompleted / totalModules) * 100) : 0;
                    const allComplete = totalCompleted === totalModules && totalModules > 0;

                    return (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`
                                rounded-2xl p-8 border-4 flex flex-col md:flex-row items-center gap-8 transition-all relative overflow-hidden
                                ${allComplete
                                    ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-300 shadow-lg'
                                    : 'bg-slate-50 border-dashed border-slate-300'
                                }
                            `}
                        >
                            {/* Golden glow for completed */}
                            {allComplete && (
                                <div className="absolute inset-0 bg-gradient-to-br from-yellow-200/20 to-amber-300/20 pointer-events-none" />
                            )}

                            {/* Progress Ring */}
                            <div className="relative z-10">
                                <ProgressRing
                                    progress={overallProgress}
                                    size={100}
                                    strokeWidth={8}
                                    color={allComplete ? '#f59e0b' : '#64748b'}
                                    backgroundColor={allComplete ? '#fef3c7' : '#e2e8f0'}
                                />
                            </div>

                            {/* Info */}
                            <div className="flex-1 text-center md:text-left relative z-10">
                                <h3 className={`text-2xl font-black font-comic mb-2 ${allComplete ? 'text-amber-700' : 'text-slate-500'}`}>
                                    {allComplete ? '🎉 Master Scout Achievement Unlocked!' : 'Journey to Master Scout'}
                                </h3>
                                <p className="text-slate-600 mb-3">
                                    {allComplete
                                        ? 'Congratulations! You have completed ALL courses and earned the prestigious Master Scout Diploma.'
                                        : `Complete all ${totalModules} modules across ${COURSES.length} courses to earn the ultimate achievement.`
                                    }
                                </p>

                                {/* Mini progress grid */}
                                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                                    {COURSES.map((course, i) => {
                                        const stat = courseStats[i];
                                        const isComplete = stat.completed === stat.total && stat.total > 0;
                                        return (
                                            <div
                                                key={course.id}
                                                className={`px-2 py-1 rounded text-xs font-bold ${isComplete ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}
                                            >
                                                {isComplete ? '✓' : `${stat.completed}/${stat.total}`} {course.title.replace(/Course \d+: /, '')}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Action Button */}
                            <div className="relative z-10">
                                {allComplete ? (
                                    <button
                                        onClick={() => navigate('/diploma')}
                                        className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-xl font-black text-lg shadow-xl hover:shadow-amber-300/50 hover:scale-105 transition-all active:scale-95"
                                    >
                                        <Printer size={22} />
                                        Print Diploma
                                    </button>
                                ) : (
                                    <button disabled className="px-8 py-4 bg-slate-200 text-slate-400 rounded-xl font-bold cursor-not-allowed">
                                        <Lock size={18} className="inline mr-2" />
                                        Complete All Courses
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    );
                })()}
            </div>

            {/* Certifications Section */}
            <div>
                <h2 className="text-2xl font-bold text-slate-700 mb-6 flex items-center gap-2 border-t border-slate-200 pt-8 mt-8">
                    <BookOpen className="text-blue-500" />
                    Proficiency Certifications
                </h2>

                <div className="grid grid-cols-1 gap-4">
                    {COURSES.map(course => {
                        const status = getCourseStatus(course.id);
                        const IconComponent = getCourseIcon(course.id as any);

                        return (
                            <div
                                key={course.id}
                                className={`
                                    rounded-xl p-6 border-2 flex flex-col md:flex-row items-center gap-6 transition-all
                                    ${status.isComplete ? 'bg-white shadow-sm hover:shadow-md' : 'bg-slate-50 border-slate-200 border-dashed opacity-80'}
                                `}
                                style={{ borderColor: status.isComplete ? 'transparent' : '' }}
                            >
                                {/* Certificate Icon Stub */}
                                <div className={`
                                    w-32 h-24 flex-shrink-0 rounded-xl border-4 flex flex-col items-center justify-center relative overflow-hidden transition-transform duration-300 hover:scale-105 shadow-inner
                                    ${status.isComplete ? `bg-gradient-to-br ${course.gradient} border-white shadow-lg` : 'bg-slate-200 border-slate-300 text-slate-400'}
                                `}>
                                    {status.isComplete ? (
                                        <div className="text-center text-white p-2 w-full h-full flex flex-col items-center justify-center relative">
                                            {/* Subdued background icon */}
                                            <IconComponent size={64} className="absolute opacity-20" />
                                            {/* Foreground text */}
                                            <div className="text-[10px] font-serif font-black tracking-widest uppercase opacity-90 drop-shadow-md z-10 w-full truncate px-1">{course.id.replace(/-/g, ' ')}</div>
                                            <Award size={28} className="mx-auto my-1 text-white drop-shadow-md z-10" />
                                            <div className="text-[9px] font-mono font-bold tracking-widest bg-white/20 px-2 py-0.5 rounded backdrop-blur-sm z-10 shadow-sm">CERTIFIED</div>
                                        </div>
                                    ) : (
                                        <div className="text-center text-slate-400 opacity-50 flex flex-col items-center justify-center">
                                            <Lock size={28} className="mx-auto mb-1" />
                                            <div className="text-[10px] font-bold tracking-widest">LOCKED</div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1 text-center md:text-left">
                                    <h3 className={`text-xl font-comic font-bold tracking-wide ${status.isComplete ? 'text-slate-800' : 'text-slate-400'}`}>
                                        Certificate of Proficiency: {course.title}
                                    </h3>
                                    <p className="text-slate-500 text-sm mb-3">
                                        {status.isComplete
                                            ? `Demonstrated mastery in: ${course.description}`
                                            : `Complete all ${status.total} missions to earn this professional certification.`
                                        }
                                    </p>

                                    {/* Progress Bar for Incomplete */}
                                    {!status.isComplete && (
                                        <div className="w-full max-w-md bg-slate-200 rounded-full h-2 mb-2 overflow-hidden shadow-inner">
                                            <div
                                                className={`h-2 rounded-full transition-all duration-1000 bg-gradient-to-r ${course.gradient}`}
                                                style={{ width: `${(status.count / status.total) * 100}%` }}
                                            />
                                        </div>
                                    )}

                                    <div className="text-xs font-mono text-slate-400 uppercase tracking-widest">
                                        Status: {status.isComplete ? (
                                            <span className="text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded border border-green-200">Active Qualification</span>
                                        ) : (
                                            `${status.count}/${status.total} Modules Completed`
                                        )}
                                    </div>
                                </div>

                                <div>
                                    {status.isComplete ? (
                                        <button
                                            onClick={() => navigate(`/certificate/${course.id}`)}
                                            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg hover:bg-blue-700 hover:shadow-blue-200/50 transition-all active:scale-95"
                                        >
                                            <Printer size={18} />
                                            Print Certificate
                                        </button>
                                    ) : (
                                        <button disabled className="px-6 py-3 bg-slate-200 text-slate-400 rounded-xl font-bold cursor-not-allowed">
                                            Locked
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
